import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { FormControl, ReactiveFormsModule, Validators } from '@angular/forms';

export interface YearsMonthsValue {
  years: number | null;
  months: number | null;
  days?: number | null;
}

type YearsMonthsSize = 'sm' | 'md' | 'lg';

@Component({
  selector: 'app-years-months-reusable',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule],
  templateUrl: './years-months-reusable.html',
})
export class YearsMonthsReusable implements OnInit, OnChanges {
  @Input() label = '';
  @Input() required = false;
  @Input() disabled = false;
  @Input() size: YearsMonthsSize = 'md';
  @Input() yearsControl: FormControl<number | null> = new FormControl<number | null>(null);
  @Input() monthsControl: FormControl<number | null> = new FormControl<number | null>(null);
  // Days is opt-in (used by mortgage product term only); other usages stay years+months.
  @Input() showDays = false;
  @Input() daysControl: FormControl<number | null> = new FormControl<number | null>(null);
  @Input() yearsPlaceholder = 'years';
  @Input() monthsPlaceholder = 'months';
  @Input() daysPlaceholder = 'days';
  @Input() yearsErrorMessage = 'Years is required.';
  @Input() monthsErrorMessage = 'Months is required.';
  @Input() daysErrorMessage = 'Days is required.';

  @Output() valueChange = new EventEmitter<YearsMonthsValue>();

  ngOnInit(): void {
    this.applyValidators();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['required'] ||
      changes['yearsControl'] ||
      changes['monthsControl'] ||
      changes['daysControl'] ||
      changes['showDays']
    ) {
      this.applyValidators();
    }
  }

  /** Blocks characters that are meaningless for a non-negative whole-number field. */
  protected preventNegative(event: KeyboardEvent): void {
    if (['-', '+', 'e', 'E'].includes(event.key)) {
      event.preventDefault();
    }
  }

  /** Clamps years to 0-99 as the user types. */
  protected onYearsChange(event?: Event): void {
    const value = this.yearsControl.value;
    if (value !== null && value !== undefined) {
      let clamped = value;
      if (value < 0) {
        clamped = 0;
      } else if (value > 99) {
        clamped = 99;
      }
      if (clamped !== value) {
        this.yearsControl.setValue(clamped, { emitEvent: false });
        if (event instanceof InputEvent) {
          (event.target as HTMLInputElement).value = String(clamped);
        }
      }
    }
    this.emitChange();
  }

  /** Clamps months to 0-11 as the user types, mirroring the years/months split used elsewhere in the app. */
  protected onMonthsChange(event?: Event): void {
    const value = this.monthsControl.value;
    if (value !== null && value !== undefined) {
      let clamped = value;
      if (value < 0) {
        clamped = 0;
      } else if (value > 11) {
        clamped = 11;
      }
      if (clamped !== value) {
        this.monthsControl.setValue(clamped, { emitEvent: false });
        if (event instanceof InputEvent) {
          (event.target as HTMLInputElement).value = String(clamped);
        }
      }
    }
    this.emitChange();
  }

  /** Clamps days to 0-30 as the user types (remainder days after whole months). */
  protected onDaysChange(event?: Event): void {
    const value = this.daysControl.value;
    if (value !== null && value !== undefined) {
      let clamped = value;
      if (value < 0) {
        clamped = 0;
      } else if (value > 30) {
        clamped = 30;
      }
      if (clamped !== value) {
        this.daysControl.setValue(clamped, { emitEvent: false });
        if (event instanceof InputEvent) {
          (event.target as HTMLInputElement).value = String(clamped);
        }
      }
    }
    this.emitChange();
  }

  protected get inputClassMap(): Record<string, boolean> {
    return {
      'h-[46px] py-2.5 placeholder:text-[14px]': this.size === 'lg',
      'h-10 py-2 placeholder:text-[14px]': this.size === 'md',
      'h-9 py-1.5 placeholder:text-[14px]': this.size === 'sm',
      '!text-sm text-slate900': this.size === 'lg',
      '!text-[14px] text-slate900': this.size === 'md',
      '!text-[13px] text-slate900': this.size === 'sm',
      'pl-3': true,
      'pr-3': true,
    };
  }

  protected get yearsShouldShowError(): boolean {
    return this.yearsControl.invalid && (this.yearsControl.dirty || this.yearsControl.touched);
  }

  protected get monthsShouldShowError(): boolean {
    return this.monthsControl.invalid && (this.monthsControl.dirty || this.monthsControl.touched);
  }

  protected get daysShouldShowError(): boolean {
    return (
      this.showDays &&
      this.daysControl.invalid &&
      (this.daysControl.dirty || this.daysControl.touched)
    );
  }

  private applyValidators(): void {
    this.yearsControl.setValidators(
      this.required
        ? [Validators.required, Validators.min(0), Validators.max(99)]
        : [Validators.min(0), Validators.max(99)],
    );
    this.monthsControl.setValidators(
      this.required
        ? [Validators.required, Validators.min(0), Validators.max(11)]
        : [Validators.min(0), Validators.max(11)],
    );
    this.yearsControl.updateValueAndValidity({ emitEvent: false });
    this.monthsControl.updateValueAndValidity({ emitEvent: false });

    if (this.showDays) {
      this.daysControl.setValidators(
        this.required
          ? [Validators.required, Validators.min(0), Validators.max(30)]
          : [Validators.min(0), Validators.max(30)],
      );
      this.daysControl.updateValueAndValidity({ emitEvent: false });
    }
  }

  private emitChange(): void {
    this.valueChange.emit({
      years: this.yearsControl.value,
      months: this.monthsControl.value,
      ...(this.showDays ? { days: this.daysControl.value } : {}),
    });
  }
}
